/**
 * Panel windowing over a composed production SVG.
 *
 * The composite encodes all export fix-ups (global bottom-left flip, pattern
 * matrix restores, pattern rotations), so panels are carved by VIEWBOX
 * WINDOWING — never by re-serializing fabric objects. A panel window is the
 * panel's canvas-space box mapped into the composite's (flipped) viewBox
 * space, plus a small bleed.
 *
 * The same windows drive the nested-sheet builder: each placement is a nested
 * `<svg viewBox="{window}">` showing just that panel of a row's artwork.
 */
import type { OverlayBox, PanelBox } from '@/composables/useProductionFileEngine';
import {
  IDENTITY,
  multiplyMat,
  parseTransform,
  unionPathData,
  type Mat,
  type PathSpec,
} from './clipFlatten';
import { extractSvgContent, namespaceSvgIds } from './logoVectorize';

export interface PanelWindow {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Small safety margin around a panel window so strokes/antialiasing at the
 * panel edge aren't shaved. Pieces are isolated per panel, so this no longer
 * needs to guard against neighbor artwork — keep it tight (≈0.1cm at 2048
 * canvas / typical atlas scale) or nesting wastes paper.
 */
export const DEFAULT_BLEED_FRACTION = 0.0015;

/**
 * Map a canvas-space panel box (pre-flip, y down from the top of the fabric
 * canvas) into the composite's viewBox space. The export wraps everything in
 * `scale(1,-1) translate(0, -H)`, so canvas y ∈ [top, top+h] renders at
 * viewBox y ∈ [H-(top+h), H-top].
 */
export function computePanelWindow(
  panel: Pick<PanelBox, 'left' | 'top' | 'width' | 'height'>,
  viewBoxSize: number,
  bleedFraction: number = DEFAULT_BLEED_FRACTION
): PanelWindow {
  const bleed = viewBoxSize * bleedFraction;

  const rawX = panel.left - bleed;
  const rawY = viewBoxSize - (panel.top + panel.height) - bleed;
  const rawRight = panel.left + panel.width + bleed;
  const rawBottom = viewBoxSize - panel.top + bleed;

  const x = Math.max(0, rawX);
  const y = Math.max(0, rawY);
  const right = Math.min(viewBoxSize, rawRight);
  const bottom = Math.min(viewBoxSize, rawBottom);

  return { x, y, width: Math.max(0, right - x), height: Math.max(0, bottom - y) };
}

/** Physical size of a window in points. The atlas can be anisotropic (its pt
 *  width/height differ), so each axis converts with its own scale. */
export function windowSizePt(
  window: PanelWindow,
  viewBoxSize: number,
  compositeWidthPt: number,
  compositeHeightPt: number
): { widthPt: number; heightPt: number } {
  return {
    widthPt: (window.width / viewBoxSize) * compositeWidthPt,
    heightPt: (window.height / viewBoxSize) * compositeHeightPt,
  };
}

export interface CompositeContent {
  /** Root <svg …> attributes minus width/height/viewBox (namespaces survive). */
  rootAttrs: string;
  /** Inner markup of the composite (defs + flipped content group). */
  inner: string;
}

/** Pull the composite apart once so windows/sheets can re-wrap its content. */
export function extractCompositeContent(compositeSvg: string): CompositeContent | null {
  const extracted = extractSvgContent(compositeSvg);
  if (!extracted) return null;

  const openTag = compositeSvg.match(/<svg\b[^>]*>/i)?.[0] ?? '<svg>';
  const rootAttrs = openTag
    .replace(/^<svg/i, '')
    .replace(/>$/, '')
    .replace(/\b(width|height|viewBox)\s*=\s*"[^"]*"/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  return { rootAttrs, inner: extracted.inner };
}

/**
 * Namespace one row's composite content so several rows can coexist in a
 * sheet document without id collisions (gradients, clipPaths, patterns).
 */
export function prepareRowContent(compositeInner: string, rowIndex: number): string {
  return namespaceSvgIds(compositeInner, `prow${rowIndex}_`);
}

/** Standalone single-panel SVG (window over the full composite content). */
export function buildPanelSvg(
  content: CompositeContent,
  window: PanelWindow,
  sizePt: { widthPt: number; heightPt: number }
): string {
  return (
    `<svg ${content.rootAttrs} width="${round(sizePt.widthPt)}pt" height="${round(
      sizePt.heightPt
    )}pt" viewBox="${round(window.x)} ${round(window.y)} ${round(window.width)} ${round(
      window.height
    )}">` +
    content.inner +
    '</svg>'
  );
}

export interface PanelPieceContent {
  panel: PanelBox;
  window: PanelWindow;
  /**
   * Inner markup for this piece: the composite's wrapper chain (flip group,
   * design transform) with ONLY this panel's subtree kept, plus the
   * name/number/logo overlays whose boxes intersect the panel window, plus
   * pattern groups (they self-clip to their zone). `true` isolation — panel
   * bboxes overlap each other in real atlases, so windowing the full artwork
   * would drag neighbor panels into every piece.
   */
  inner: string;
  /** True when isolation wasn't possible and the full artwork is windowed. */
  windowedFallback: boolean;
}

function rectsIntersect(
  a: { left: number; top: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
  viewBoxSize: number
): boolean {
  // Overlay boxes are canvas-space (y down, pre-flip); windows are viewBox
  // space (post-flip). Flip the overlay into window space first.
  const oy = viewBoxSize - (a.top + a.height);
  return !(
    a.left > b.x + b.width ||
    a.left + a.width < b.x ||
    oy > b.y + b.height ||
    oy + a.height < b.y
  );
}

/**
 * Carve the composite into per-panel piece contents. Falls back to whole-
 * artwork windows (previous behavior) when a panel has no serialized id or
 * the document doesn't match the expected fabric structure.
 */
export function buildPanelPieceContents(
  compositeSvg: string,
  panels: PanelBox[],
  overlays: OverlayBox[],
  viewBoxSize: number,
  bleedFraction: number = DEFAULT_BLEED_FRACTION
): PanelPieceContent[] {
  const full = extractCompositeContent(compositeSvg);
  const fullInner = full?.inner ?? '';

  const windowed = (panel: PanelBox): PanelPieceContent => ({
    panel,
    window: computePanelWindow(panel, viewBoxSize, bleedFraction),
    inner: fullInner,
    windowedFallback: true,
  });

  const doc = new DOMParser().parseFromString(compositeSvg, 'image/svg+xml');
  const root = doc.documentElement;
  if (doc.getElementsByTagName('parsererror').length || root.tagName.toLowerCase() !== 'svg') {
    return panels.map(windowed);
  }

  // The flip wrapper generateProductionSVG adds around everything.
  const flip = Array.from(root.children).find(
    (el) =>
      el.tagName.toLowerCase() === 'g' &&
      /scale\(1,\s*-1\)/.test(el.getAttribute('transform') ?? '')
  );
  if (!flip) return panels.map(windowed);

  // Flip children in document order = canvas objects in add order (plus
  // <desc>/<defs> fabric emits first). The design group is the child that
  // contains the panel ids; everything after it is a logo/text overlay.
  const flipChildren = Array.from(flip.children).filter(
    (el) => !['desc', 'defs'].includes(el.tagName.toLowerCase())
  );
  const designNode = flipChildren.find((el) =>
    panels.some((p) => p.sourceId && el.querySelector(`[id="${cssAttrEscape(p.sourceId)}"]`))
  );
  if (!designNode) return panels.map(windowed);

  const overlayNodes = flipChildren.filter((el) => el !== designNode);
  // Positional mapping between overlay elements and their canvas-space boxes
  // requires a 1:1 order match — bail to window mode when they disagree.
  const overlaysAligned = overlayNodes.length === overlays.length;

  return panels.map((panel) => {
    if (!panel.sourceId || !overlaysAligned) return windowed(panel);

    let window = computePanelWindow(panel, viewBoxSize, bleedFraction);
    const clone = root.cloneNode(true) as Element;

    const target = clone.querySelector(`[id="${cssAttrEscape(panel.sourceId)}"]`);
    if (!target) return windowed(panel);

    const cloneFlip = Array.from(clone.children).find(
      (el) =>
        el.tagName.toLowerCase() === 'g' &&
        /scale\(1,\s*-1\)/.test(el.getAttribute('transform') ?? '')
    );
    if (!cloneFlip) return windowed(panel);

    // Prune inside the design subtree: walking up from the panel to the flip
    // wrapper, drop every sibling except pattern groups (they self-clip to
    // their zone, so keeping them paints correct pattern fill on this panel).
    let node: Element = target;
    while (node.parentElement && node !== cloneFlip) {
      const parent: Element = node.parentElement;
      if (parent !== cloneFlip) {
        for (const sibling of Array.from(parent.children)) {
          if (sibling === node) continue;
          const tag = sibling.tagName.toLowerCase();
          if (tag === 'desc' || tag === 'defs') continue;
          const id =
            sibling.getAttribute('id') ?? sibling.querySelector('[id]')?.getAttribute('id') ?? '';
          if (id.startsWith('pattern_group_')) continue;
          sibling.remove();
        }
      }
      node = parent;
    }

    const designInClone = Array.from(cloneFlip.children).find((el) => el.contains(target));
    const cloneOverlayNodes = Array.from(cloneFlip.children).filter(
      (el) => !['desc', 'defs'].includes(el.tagName.toLowerCase()) && el !== designInClone
    );

    // Which overlays belong to this panel is decided against the panel's
    // TIGHT fabric bounds, measured on the isolated subtree alone. The
    // panel's fabric-object bbox is no good for that: atlas groups carry a
    // background shape clipped to the silhouette, so the bbox spans most of
    // the artboard and every neighbouring name/number/logo would be dragged
    // into this piece (and float in its blank area on the sheet).
    const fabricOnly = clone.cloneNode(true) as Element;
    const probeFlip = findFlipWrapper(fabricOnly);
    const probeTarget = fabricOnly.querySelector(`[id="${cssAttrEscape(panel.sourceId)}"]`);
    if (probeFlip && probeTarget) {
      const keep = Array.from(probeFlip.children).find((el) => el.contains(probeTarget));
      Array.from(probeFlip.children).forEach((el) => {
        const tag = el.tagName.toLowerCase();
        if (el !== keep && tag !== 'desc' && tag !== 'defs') el.remove();
      });
      const probe = buildPieceStandaloneSvg(full?.rootAttrs ?? '', window, fabricOnly.innerHTML, 320);
      const bounds = measurePieceGeometryBounds(probe.svg).all;
      if (bounds) window = windowAroundBounds(bounds, 0, window);
    }

    // Overlays (flip-level siblings of the design node): keep only the ones
    // whose canvas box intersects this panel's window. Survivors are tagged so
    // measurement/silhouette passes can tell fabric from names/numbers/logos.
    cloneOverlayNodes.forEach((el, i) => {
      const box = overlays[i];
      if (!box || !rectsIntersect(box, window, viewBoxSize)) el.remove();
      else el.setAttribute('data-production-overlay', '1');
    });

    const inner = clone.innerHTML;
    if (!inner) return windowed(panel);
    return { panel, window, inner, windowedFallback: false };
  });
}

function cssAttrEscape(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}

/** The `scale(1,-1)` flip wrapper generateProductionSVG adds around everything. */
function findFlipWrapper(root: Element): Element | undefined {
  return Array.from(root.children).find(
    (el) =>
      el.tagName.toLowerCase() === 'g' &&
      /scale\(1,\s*-1\)/.test(el.getAttribute('transform') ?? '')
  );
}

/** Minimum font size of the `<SIZE>-<qty>` header printed above a composite. */
export const HEADER_LABEL_FONT_PT = 120;
/** Header font size as a share of the composite's physical height (the factory's own sheets use ~6%). */
export const HEADER_LABEL_HEIGHT_FRACTION = 0.06;
export const HEADER_LABEL_COLOR = '#4d4d4d';

/**
 * Print a header ("MEN 4XL-1": size and quantity) above a composite production
 * SVG: the viewBox grows upward by a band, the physical height with it, and
 * the text is appended after the flip wrapper so it renders upright. Font
 * size defaults to a fraction of the sheet height (never below the minimum).
 */
export function addHeaderLabelToComposite(
  compositeSvg: string,
  text: string,
  fontPtOverride?: number
): string {
  const label = text.trim();
  if (!label) return compositeSvg;
  const open = compositeSvg.match(/<svg\b[^>]*>/i);
  if (!open || open.index === undefined) return compositeSvg;
  const tag = open[0];
  const vb = tag
    .match(/viewBox="([^"]+)"/i)?.[1]
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  const heightPt = Number(tag.match(/\sheight="([\d.]+)"/i)?.[1]);
  if (!vb || vb.length !== 4 || vb.some((n) => !Number.isFinite(n))) return compositeSvg;
  if (!Number.isFinite(heightPt) || heightPt <= 0 || vb[3] <= 0) return compositeSvg;
  const [x, y, w, h] = vb;
  const ptPerUnit = heightPt / h;
  const fontPt =
    fontPtOverride ?? Math.max(HEADER_LABEL_FONT_PT, heightPt * HEADER_LABEL_HEIGHT_FRACTION);
  const fontUnits = fontPt / ptPerUnit;
  const band = fontUnits * 1.4;

  const newTag = tag
    .replace(
      /viewBox="[^"]+"/i,
      `viewBox="${round(x)} ${round(y - band)} ${round(w)} ${round(h + band)}"`
    )
    .replace(/\sheight="[\d.]+"/i, ` height="${round(heightPt + band * ptPerUnit)}"`);
  const escaped = label.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const textEl =
    `<text x="${round(x + fontUnits * 0.25)}" y="${round(y - band + fontUnits * 1.05)}"` +
    ` font-family="Arial, Helvetica, sans-serif" font-weight="bold" font-size="${round(fontUnits)}"` +
    ` fill="${HEADER_LABEL_COLOR}" xml:space="preserve">${escaped}</text>`;

  const closeIndex = compositeSvg.lastIndexOf('</svg>');
  if (closeIndex < 0) return compositeSvg;
  return (
    compositeSvg.slice(0, open.index) +
    newTag +
    compositeSvg.slice(open.index + tag.length, closeIndex) +
    textEl +
    compositeSvg.slice(closeIndex)
  );
}

function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/** Window-space point where a piece's size label baseline should sit. */
export interface LabelAnchor {
  x: number;
  y: number;
}

/**
 * Size-label markup: black fill over a white outline, emitted as TWO stacked
 * <text> elements (stroke first, fill on top) so legibility doesn't depend on
 * `paint-order`, which CorelDRAW's SVG import ignores. Always UPPERCASE: the
 * baseline sits exactly on the fabric edge, so lowercase descenders (the y in
 * "Youth S") would dip past the edge and get cut off.
 */
export function sizeLabelMarkup(
  x: number,
  y: number,
  text: string,
  fontSize: number,
  strokeWidth: number
): string {
  const escaped = text
    .toUpperCase()
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const common =
    ` font-family="Arial, Helvetica, sans-serif" font-size="${round(fontSize)}"` +
    ` font-weight="bold" text-anchor="middle"`;
  return (
    `<text x="${round(x)}" y="${round(y)}"${common} fill="none" stroke="#ffffff"` +
    ` stroke-width="${round(strokeWidth)}" stroke-linejoin="round">${escaped}</text>` +
    `<text x="${round(x)}" y="${round(y)}"${common} fill="#000000">${escaped}</text>`
  );
}

/** Standalone svg for one piece (used for rendering/measuring). */
export function buildPieceStandaloneSvg(
  rootAttrs: string,
  window: PanelWindow,
  inner: string,
  pixelWidth = 160
): { svg: string; pixelWidth: number; pixelHeight: number } {
  const pixelHeight = Math.max(1, Math.round((pixelWidth * window.height) / window.width) || 1);
  const svg =
    `<svg ${rootAttrs} width="${pixelWidth}" height="${pixelHeight}" ` +
    `viewBox="${round(window.x)} ${round(window.y)} ${round(window.width)} ${round(window.height)}">` +
    inner +
    '</svg>';
  return { svg, pixelWidth, pixelHeight };
}

export interface GeometryBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * EXACT geometric bounds of an isolated piece, measured with getBBox on a
 * hidden mounted copy (curves evaluated by the SVG engine — no raster
 * quantization; the earlier alpha-scan approach was off by a scan pixel,
 * which is millimetres at garment scale). Returns window/viewBox-space
 * bounds for the fabric alone (`design`, overlays hidden via display:none —
 * getBBox skips display:none subtrees) and for everything (`all`). Both null
 * where getBBox is unavailable (jsdom).
 *
 * Pattern groups are excluded from BOTH measurements: they are kept in every
 * piece (they self-clip to their zone) but getBBox ignores clipping, so
 * their full backing rect would balloon the bounds far past this panel.
 */
export function measurePieceGeometryBounds(pieceSvg: string): {
  design: GeometryBounds | null;
  all: GeometryBounds | null;
} {
  const none = { design: null, all: null };
  if (typeof document === 'undefined' || !document.body) return none;
  const parsed = new DOMParser().parseFromString(pieceSvg, 'image/svg+xml');
  if (parsed.getElementsByTagName('parsererror').length) return none;

  const host = document.createElement('div');
  host.style.cssText =
    'position:absolute;left:-100000px;top:0;width:0;height:0;overflow:hidden;visibility:hidden;';
  const svgEl = document.importNode(parsed.documentElement, true) as unknown as SVGSVGElement;
  host.appendChild(svgEl as unknown as Node);
  document.body.appendChild(host);
  try {
    const bbox = (): GeometryBounds | null => {
      try {
        const b = svgEl.getBBox();
        if (!b || !Number.isFinite(b.width) || b.width < 0) return null;
        return { x: b.x, y: b.y, width: b.width, height: b.height };
      } catch {
        return null;
      }
    };
    for (const el of Array.from(svgEl.querySelectorAll('[id*="pattern_group"]'))) {
      (el as SVGElement).style.display = 'none';
    }
    const all = bbox();
    const overlayEls = Array.from(svgEl.querySelectorAll('[data-production-overlay]'));
    for (const el of overlayEls) (el as SVGElement).style.display = 'none';
    const design = overlayEls.length ? bbox() : all;
    return { design, all };
  } catch {
    return none;
  } finally {
    host.remove();
  }
}

/**
 * Label baseline: bottom-center of the piece's fabric bounds (fallback:
 * window), lifted by the label margin so the glyphs sit just above the
 * panel's exact bottom edge.
 */
export function computeLabelAnchor(
  window: PanelWindow,
  bounds: GeometryBounds | null,
  marginUnits: number
): LabelAnchor {
  const region = bounds ?? window;
  return {
    x: region.x + region.width / 2,
    y: Math.max(region.y, region.y + region.height - marginUnits),
  };
}

/**
 * The piece window is the measured content bounds plus a small bleed —
 * NOT clamped to the panel's fabric-object bbox. Panel bboxes routinely
 * carry large empty margins (phantom padding) and, on GRADED rows, come out
 * slightly SMALLER than the actually rendered fabric (the XL outline bug:
 * clamping to the stale box cut ~1% of the fabric out of the window and its
 * cut silhouette). The measured geometry is the truth; the bleed covers
 * painted stroke halves (getBBox is geometry-only).
 */
export function windowAroundBounds(
  bounds: GeometryBounds | null,
  bleedUnits: number,
  fallback: PanelWindow
): PanelWindow {
  if (!bounds || bounds.width <= 0 || bounds.height <= 0) return fallback;
  return {
    x: bounds.x - bleedUnits,
    y: bounds.y - bleedUnits,
    width: bounds.width + 2 * bleedUnits,
    height: bounds.height + 2 * bleedUnits,
  };
}

/**
 * Cut silhouette of one piece — the true union of its fabric paths (overlays
 * excluded), clipped to the piece window, expressed in piece-LOCAL POINTS so
 * outline strokes are physically exact millimetres. Null when it can't be
 * computed (no paths, paper.js unavailable).
 */
async function computePieceSilhouettePt(
  pieceSvg: string,
  window: PanelWindow,
  viewBoxSize: number,
  compositeWidthPt: number,
  compositeHeightPt: number
): Promise<string | null> {
  // window units → piece-local pt (anisotropic axes convert independently).
  const sx = compositeWidthPt / viewBoxSize;
  const sy = compositeHeightPt / viewBoxSize;
  const post: Mat = [sx, 0, 0, sy, -window.x * sx, -window.y * sy];
  return computePieceSilhouette(pieceSvg, window, post);
}

/**
 * Union of a piece's fabric geometry (overlays, patterns' backing rects and
 * defs excluded), clipped to the window, then mapped through `post`
 * (IDENTITY keeps composite viewBox units).
 */
async function computePieceSilhouette(
  pieceSvg: string,
  window: PanelWindow,
  post: Mat
): Promise<string | null> {
  const doc = new DOMParser().parseFromString(pieceSvg, 'image/svg+xml');
  if (doc.getElementsByTagName('parsererror').length) return null;

  const skip = new Set(['defs', 'desc', 'title', 'metadata', 'clippath', 'pattern', 'text']);
  const shapes: PathSpec[] = [];
  const walk = (el: Element, matrix: Mat): void => {
    for (const child of Array.from(el.children)) {
      const tag = child.tagName.toLowerCase();
      if (skip.has(tag) || child.hasAttribute('data-production-overlay')) continue;
      // Pattern groups self-clip to their zone but the raw geometry is a big
      // backing rect — unioning it would balloon the silhouette to the whole
      // window. The zone's base shape carries the panel geometry instead.
      if ((child.getAttribute('id') ?? '').includes('pattern_group')) continue;
      const m = multiplyMat(matrix, parseTransform(child.getAttribute('transform')));
      if (tag === 'path') {
        const d = child.getAttribute('d');
        if (d) shapes.push({ d, matrix: m, fillRule: pathFillRule(child) });
      } else if (tag === 'g' || tag === 'a') {
        walk(child, m);
      }
      // Basic shapes were already converted to paths; anything else
      // (image/use) has no path geometry to contribute.
    }
  };
  walk(doc.documentElement, IDENTITY);
  if (!shapes.length) return null;

  // Pattern groups are kept in every piece and may carry other panels' zone
  // shapes — clipping the union to the window discards anything foreign.
  const clip: PathSpec = {
    d: `M${window.x} ${window.y}h${window.width}v${window.height}h${-window.width}z`,
    matrix: IDENTITY,
    fillRule: 'nonzero',
  };
  return unionPathData(shapes, post, clip);
}

function pathFillRule(el: Element): 'nonzero' | 'evenodd' {
  const attr = el.getAttribute('fill-rule');
  if (attr === 'evenodd') return 'evenodd';
  const style = el.getAttribute('style') ?? '';
  return /(?:^|;)\s*fill-rule\s*:\s*evenodd/.test(style) ? 'evenodd' : 'nonzero';
}

export interface PanelPlacementOptions {
  /**
   * Also compute each piece's cut silhouette in piece-local points (needs the
   * composite's physical size) — enables the nested-sheet laser outlines.
   */
  silhouettePt?: { compositeWidthPt: number; compositeHeightPt: number };
  /**
   * Also compute each piece's cut silhouette in the composite's own viewBox
   * space — enables per-panel clip paths and laser outlines ON the composite.
   */
  silhouetteViewBox?: boolean;
}

export interface PanelLabelPlacement {
  piece: PanelPieceContent;
  anchor: LabelAnchor;
  /** Cut silhouette in piece-local pt, when requested and computable. */
  outlinePtD?: string | null;
  /** Cut silhouette in composite viewBox units, when requested and computable. */
  outlineViewBoxD?: string | null;
}

/**
 * Isolate every panel, tighten each piece's window to its exact geometric
 * bounds (so the piece frame hugs the fabric instead of the panel's padded
 * bbox) and place the size label from the panel's exact bottom edge.
 */
export async function computePanelLabelPlacements(
  compositeSvg: string,
  panels: PanelBox[],
  overlays: OverlayBox[],
  viewBoxSize: number,
  marginUnits: number,
  options: PanelPlacementOptions = {}
): Promise<PanelLabelPlacement[]> {
  const rootAttrs = extractCompositeContent(compositeSvg)?.rootAttrs ?? '';
  const pieces = buildPanelPieceContents(compositeSvg, panels, overlays, viewBoxSize);

  const placements: PanelLabelPlacement[] = [];
  for (const piece of pieces) {
    const { svg } = buildPieceStandaloneSvg(rootAttrs, piece.window, piece.inner, 320);
    // Only isolated pieces are measured — a windowed fallback still contains
    // neighbor artwork, so its bounds don't describe this panel alone.
    const geo = piece.windowedFallback
      ? { design: null, all: null }
      : measurePieceGeometryBounds(svg);
    // ZERO bleed: the frame is the exact fabric bounds. The old percentage
    // bleed (0.15% of garment ≈ 2mm+ per side) sat invisibly between fabric
    // and frame, inflating every nested gap past the configured padding.
    // Sub-millimetre paint overhang (overlay stroke halves — getBBox is
    // geometry-only) lands harmlessly in the outline zone; sheets don't clip.
    const window = piece.windowedFallback
      ? piece.window
      : windowAroundBounds(geo.all, 0, piece.window);
    const anchor = computeLabelAnchor(window, geo.design, marginUnits);

    let outlinePtD: string | null | undefined;
    if (options.silhouettePt && !piece.windowedFallback) {
      outlinePtD = await computePieceSilhouettePt(
        svg,
        window,
        viewBoxSize,
        options.silhouettePt.compositeWidthPt,
        options.silhouettePt.compositeHeightPt
      );
    }
    let outlineViewBoxD: string | null | undefined;
    if (options.silhouetteViewBox && !piece.windowedFallback) {
      outlineViewBoxD = await computePieceSilhouette(svg, window, IDENTITY);
    }
    placements.push({ piece: { ...piece, window }, anchor, outlinePtD, outlineViewBoxD });
  }
  return placements;
}

/**
 * Print size labels onto a composite production SVG (the per-roster-row
 * download), one per panel, at the given window-space anchors. Text is
 * appended AFTER the flip wrapper so it renders upright; sizes convert from
 * physical points to viewBox units via the composite's physical dimensions.
 */
export function addSizeLabelsToComposite(
  compositeSvg: string,
  sizeText: string,
  anchors: LabelAnchor[],
  viewBoxSize: number,
  compositeWidthPt: number,
  compositeHeightPt: number,
  fontPt: number,
  outlinePt: number
): string {
  const text = sizeText.trim();
  if (!text || !anchors.length) return compositeSvg;

  const ptPerUnitY = compositeHeightPt / viewBoxSize || 1;
  const fontUnits = fontPt / ptPerUnitY;
  const strokeUnits = outlinePt / (compositeWidthPt / viewBoxSize || 1);

  const labels = anchors
    .map((a) => sizeLabelMarkup(a.x, a.y, text, fontUnits, strokeUnits))
    .join('');

  const closeIndex = compositeSvg.lastIndexOf('</svg>');
  if (closeIndex < 0) return compositeSvg;
  return compositeSvg.slice(0, closeIndex) + labels + compositeSvg.slice(closeIndex);
}
